<template>
  <div class="analytics-container">
    <h2 class="page-title">数据分析 - AdSense</h2>
    
    <!-- 操作栏 -->
    <div class="action-bar">
      <div class="action-item">
        <el-button type="success" :disabled="loading || !filteredAdsenseList.length" @click="exportToExcel">
        <el-icon><Download /></el-icon>
        导出 Excel
      </el-button>
      </div>
      <div class="action-item">
        <span class="action-label">来源：</span>
        <el-select
          v-model="sourceMode"
          style="width: 130px;"
          :disabled="loading || syncing"
          @change="loadData"
        >
          <el-option label="AdSense 实时" value="google" />
          <el-option label="数据库" value="manual" />
        </el-select>
        <el-button
          type="primary"
          plain
          :loading="syncing"
          :disabled="loading || !dateStart || !dateEnd || sourceMode !== 'google'"
          @click="handleSyncRange"
        >
          同步到数据库
        </el-button>
      </div>
      
      <!-- 渠道筛选 -->
      <div class="action-item">
        <span class="action-label">渠道：</span>
        <el-select
          v-model="selectedChannel"
          placeholder="全部"
          clearable
          style="width: 140px;"
          :disabled="loading"
          @change="handleChannelChange"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="channel in channelOptions"
            :key="channel"
            :label="channel"
            :value="channel"
          />
        </el-select>
      </div>
      <!-- 日期范围筛选 -->
      <div class="action-item">
        <span class="action-label">日期范围：</span>
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          :disabled="loading"
          @change="handleDateRangeChange"
          style="width: 300px;"
        />
        <el-button :disabled="loading" @click="clearFilter">清除筛选</el-button>
      </div>
      <!-- URL 搜索（表格内模糊匹配） -->
      <div class="action-item">
        <span class="action-label">URL 搜索：</span>
        <el-input
          v-model="urlSearchKeyword"
          placeholder="输入关键词模糊匹配 URL"
          clearable
          style="width: 260px;"
        >
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>
    </div>
    
    <!-- 表格 / 图表 Tab：请求期间整块区域 loading -->
    <div
      v-loading="loading"
      element-loading-text="正在加载 AdSense 数据..."
      class="analytics-main-loading"
    >
    <el-tabs v-model="activeTab" class="analytics-tabs">
      <el-tab-pane label="数据表格" name="table">
        <div class="table-section">
      <el-table 
        :data="filteredAdsenseList" 
        style="width: 100%"
        border
        stripe
        show-summary
        :summary-method="getSummaries"
      >
        <el-table-column prop="url" label="URL" min-width="320" class-name="url-cell">
          <template #default="scope">
            <span class="url-text">{{ scope.row.url }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="gameChannel" label="游戏渠道" width="180">
          <template #default="scope">
            {{ getRowChannelsText(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column prop="date" label="日期" width="120" />
        <el-table-column prop="revenue" label="收入" width="120" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.revenue) }}
          </template>
        </el-table-column>
        <el-table-column prop="pageView" label="页面浏览量" width="120" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.pageView) }}
          </template>
        </el-table-column>
        <el-table-column prop="pageViewRevenuePerTD" label="页面浏览量收入/TD" width="160" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.pageViewRevenuePerTD) }}
          </template>
        </el-table-column>
        <el-table-column prop="adViewCount" label="广告浏览量" width="120" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.adViewCount) }}
          </template>
        </el-table-column>
        <el-table-column prop="adViewRevenuePerTD" label="广告浏览量收入/TD" width="160" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.adViewRevenuePerTD) }}
          </template>
        </el-table-column>
        <el-table-column prop="activeView" label="活跃视图" width="120" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.activeView) }}
          </template>
        </el-table-column>
        <el-table-column prop="clickCount" label="点击次数" width="120" align="right">
          <template #default="scope">
            {{ formatNumber(scope.row.clickCount) }}
          </template>
        </el-table-column>
        <el-table-column v-if="isAdminMode" label="操作" width="100" fixed="right">
          <template #default="scope">
            <el-button
              type="danger"
              size="small"
              @click="handleDelete(scope.row.id)"
              :disabled="loading"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="table-footer">共 {{ filteredAdsenseList.length }} 条</div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="图表" name="charts">
        <div class="charts-section">
          <div class="chart-row">
            <div class="chart-card chart-card-pie">
              <div class="pie-chart-header">
                <span class="chart-title">不同渠道数据占比</span>
                <el-select v-model="pieChartDimension1" placeholder="选择维度" style="width: 160px;" @change="renderCharts">
                  <el-option label="收入" value="revenue" />
                  <el-option label="页面浏览量" value="pageView" />
                  <el-option label="广告浏览量" value="adViewCount" />
                  <el-option label="点击数" value="clickCount" />
                </el-select>
              </div>
              <div ref="pieChartRef1" class="chart-dom"></div>
            </div>
            <div class="chart-card chart-card-pie">
              <div class="pie-chart-header">
                <span class="chart-title">不同渠道数据占比</span>
                <el-select v-model="pieChartDimension2" placeholder="选择维度" style="width: 160px;" @change="renderCharts">
                  <el-option label="收入" value="revenue" />
                  <el-option label="页面浏览量" value="pageView" />
                  <el-option label="广告浏览量" value="adViewCount" />
                  <el-option label="点击数" value="clickCount" />
                </el-select>
              </div>
              <div ref="pieChartRef2" class="chart-dom"></div>
            </div>
            <div class="chart-card chart-card-pie">
              <div class="pie-chart-header">
                <span class="chart-title">不同渠道数据占比</span>
                <el-select v-model="pieChartDimension3" placeholder="选择维度" style="width: 160px;" @change="renderCharts">
                  <el-option label="收入" value="revenue" />
                  <el-option label="页面浏览量" value="pageView" />
                  <el-option label="广告浏览量" value="adViewCount" />
                  <el-option label="点击数" value="clickCount" />
                </el-select>
              </div>
              <div ref="pieChartRef3" class="chart-dom"></div>
            </div>
          </div>
          
          <!-- URL维度柱状图 -->
          <div class="bar-chart-section">
            <div class="bar-chart-header">
              <div class="bar-chart-title-wrap">
                <span class="chart-title">URL维度数据对比</span>
                <span v-if="urlGroupedData.length > 25" class="bar-chart-hint">（仅展示前 25 条，共 {{ urlGroupedData.length }} 条）</span>
              </div>
              <div class="column-selector">
                <span class="selector-label">选择列：</span>
                <el-checkbox-group v-model="selectedColumns" @change="renderBarChart">
                  <el-checkbox label="revenue">收入</el-checkbox>
                  <el-checkbox label="pageView">页面浏览量</el-checkbox>
                  <el-checkbox label="pageViewRevenuePerTD">页面浏览千次（TD）收入</el-checkbox>
                  <el-checkbox label="adViewCount">广告浏览量</el-checkbox>
                  <el-checkbox label="adViewRevenuePerTD">广告千次收入</el-checkbox>
                  <el-checkbox label="activeView">活跃视图百分比</el-checkbox>
                  <el-checkbox label="clickCount">点击次数</el-checkbox>
                </el-checkbox-group>
              </div>
            </div>
            <div class="url-filter-row">
              <span class="selector-label">选择 URL：</span>
              <el-select
                v-model="selectedUrls"
                multiple
                collapse-tags
                collapse-tags-tooltip
                filterable
                placeholder="不选择则展示全部（前25条）；可搜索"
                clearable
                popper-class="url-select-dropdown"
                class="url-select-full"
                @change="renderBarChart"
              >
                <el-option
                  v-for="opt in urlOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                />
              </el-select>
              <el-button v-if="selectedUrls.length" size="small" @click="selectedUrls = []; nextTick(renderBarChart)">清空</el-button>
            </div>
            <div ref="barChartRef" class="bar-chart-dom"></div>
          </div>
          
          <div v-if="!adsenseList.length" class="charts-empty">暂无数据，请先在数据表格中加载数据</div>
        </div>
      </el-tab-pane>
      <el-tab-pane label="汇总" name="summary">
        <div class="summary-section">
          <div class="summary-desc">基于当前表格数据（共 {{ filteredAdsenseList.length }} 条）汇总；受日期范围与渠道筛选影响</div>
          <div class="summary-cards">
            <div class="summary-card">
              <div class="summary-label">收入</div>
              <div class="summary-value">{{ formatNumber(summaryTotals.revenue) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">页面浏览量</div>
              <div class="summary-value">{{ formatNumber(summaryTotals.pageView) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">广告浏览量</div>
              <div class="summary-value">{{ formatNumber(summaryTotals.adViewCount) }}</div>
            </div>
            <div class="summary-card">
              <div class="summary-label">点击次数</div>
              <div class="summary-value">{{ formatNumber(summaryTotals.clickCount) }}</div>
            </div>
          </div>
          <!-- 按日趋势折线图：根据日期筛选范围显示每日变化，支持渠道过滤 -->
          <div class="line-chart-section">
            <div class="line-chart-header">
              <div class="line-chart-title">每日趋势（收入、页面浏览、广告浏览、点击次数）</div>
              <div class="line-chart-tools">
                <el-radio-group v-model="trendViewMode" size="small">
                  <el-radio-button label="chart">图表模式</el-radio-button>
                  <el-radio-button label="table">表格模式</el-radio-button>
                </el-radio-group>
                <el-button
                  type="success"
                  size="small"
                  :disabled="!dailyTrendRows.length"
                  @click="exportDailyTrendTable"
                >
                  下载按天汇总表
                </el-button>
              </div>
            </div>
            <div v-show="trendViewMode === 'chart'" ref="lineChartRef" class="line-chart-dom"></div>
            <el-table
              v-show="trendViewMode === 'table'"
              :data="dailyTrendRows"
              border
              stripe
              style="width: 100%; margin-top: 8px;"
            >
              <el-table-column prop="date" label="日期" width="140" />
              <el-table-column prop="revenue" label="收入" min-width="120" align="right">
                <template #default="scope">{{ formatNumber(scope.row.revenue) }}</template>
              </el-table-column>
              <el-table-column prop="pageView" label="页面浏览量" min-width="120" align="right">
                <template #default="scope">{{ formatNumber(scope.row.pageView) }}</template>
              </el-table-column>
              <el-table-column prop="adViewCount" label="广告浏览量" min-width="120" align="right">
                <template #default="scope">{{ formatNumber(scope.row.adViewCount) }}</template>
              </el-table-column>
              <el-table-column prop="clickCount" label="点击次数" min-width="120" align="right">
                <template #default="scope">{{ formatNumber(scope.row.clickCount) }}</template>
              </el-table-column>
            </el-table>
          </div>
          <div v-if="!filteredAdsenseList.length" class="summary-empty">暂无数据，请先在数据表格中加载数据或调整筛选条件</div>
        </div>
      </el-tab-pane>
    </el-tabs>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Download } from '@element-plus/icons-vue'
import * as echarts from 'echarts'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { AdSenseApi } from '../../api/adsenseApi'
import { AdSenseChannelRuleApi } from '../../api/adsenseChannelRuleApi'

const route = useRoute()
// URL 包含 admin=true 时显示上传数据和删除列
const isAdminMode = computed(() => route.query.admin === 'true')

// 数据
const loading = ref(false)
const syncing = ref(false)
const adsenseList = ref([])
const dateRange = ref(null)
const urlSearchKeyword = ref('')
const activeTab = ref('table')
const pieChartRef1 = ref(null)
const pieChartRef2 = ref(null)
const pieChartRef3 = ref(null)
const barChartRef = ref(null)
const lineChartRef = ref(null)
const trendViewMode = ref('chart')
let pieChartInstance1 = null
let pieChartInstance2 = null
let pieChartInstance3 = null
let barChartInstance = null
let lineChartInstance = null

// 三个渠道饼图维度，默认：收入、页面浏览量、广告浏览量
const pieChartDimension1 = ref('revenue')
const pieChartDimension2 = ref('pageView')
const pieChartDimension3 = ref('adViewCount')
const PIE_DIMENSION_OPTIONS = {
  revenue: { label: '收入', key: 'revenue', isDecimal: true },
  pageView: { label: '页面浏览量', key: 'pageView', isDecimal: false },
  adViewCount: { label: '广告浏览量', key: 'adViewCount', isDecimal: false },
  clickCount: { label: '点击数', key: 'clickCount', isDecimal: false }
}

// 柱状图可选的列配置
const columnOptions = {
  revenue: { label: '收入', key: 'revenue' },
  pageView: { label: '页面浏览量', key: 'pageView' },
  pageViewRevenuePerTD: { label: '页面浏览千次（TD）收入', key: 'pageViewRevenuePerTD' },
  adViewCount: { label: '广告浏览量', key: 'adViewCount' },
  adViewRevenuePerTD: { label: '广告千次收入', key: 'adViewRevenuePerTD' },
  activeView: { label: '活跃视图百分比', key: 'activeView' },
  clickCount: { label: '点击次数', key: 'clickCount' }
}

// 选中的列（默认选中收入和页面浏览量）
const selectedColumns = ref(['revenue', 'pageView'])

// 柱状图 URL 维度过滤：选中的 URL（空数组表示展示全部）
const selectedUrls = ref([])
const dateStart = ref(null)
const dateEnd = ref(null)
const selectedChannel = ref('')
const sourceMode = ref('manual')
const channelRules = ref([])
const channelOptions = computed(() => channelRules.value.map(item => item.name).filter(Boolean))

// 上传对话框

// 表格 URL 模糊搜索：过滤后用于表格展示
const filteredAdsenseList = computed(() => {
  const keyword = (urlSearchKeyword.value || '').trim().toLowerCase()
  if (!keyword) return adsenseList.value
  return adsenseList.value.filter((row) => {
    const url = (row.url || '').toLowerCase()
    return url.includes(keyword)
  })
})

// 汇总：当前表格数据各数值列的总和（用于表格底部汇总行与图表汇总区）
const summaryTotals = computed(() => {
  const list = filteredAdsenseList.value
  return {
    revenue: list.reduce((s, row) => s + (Number(row.revenue) || 0), 0),
    pageView: list.reduce((s, row) => s + (Number(row.pageView) || 0), 0),
    pageViewRevenuePerTD: list.reduce((s, row) => s + (Number(row.pageViewRevenuePerTD) || 0), 0),
    adViewCount: list.reduce((s, row) => s + (Number(row.adViewCount) || 0), 0),
    adViewRevenuePerTD: list.reduce((s, row) => s + (Number(row.adViewRevenuePerTD) || 0), 0),
    activeView: list.reduce((s, row) => s + (Number(row.activeView) || 0), 0),
    clickCount: list.reduce((s, row) => s + (Number(row.clickCount) || 0), 0)
  }
})

// 取行内日期字段（兼容 date / report_date / reportDate）
function getRowDate(row) {
  const d = row.date ?? row.report_date ?? row.reportDate ?? ''
  return typeof d === 'string' ? d.trim() : (d ? String(d).trim() : '')
}

function getRowChannels(row) {
  if (Array.isArray(row?.gameChannels)) {
    return row.gameChannels.filter((item) => item && String(item).trim()).map((item) => String(item).trim())
  }
  const channel = (row?.gameChannel || '').trim()
  return channel ? [channel] : []
}

function getRowChannelsText(row) {
  const channels = getRowChannels(row)
  return channels.length ? channels.join('、') : ''
}

const loadChannelRules = async () => {
  try {
    channelRules.value = await AdSenseChannelRuleApi.getList()
  } catch (error) {
    console.error('Failed to load channel rules:', error)
    ElMessage.error('加载渠道规则失败：' + (error.response?.data?.status?.message || error.message))
  }
}


// 按日聚合：用于汇总页折线图，受日期范围与渠道筛选影响（数据源即 filteredAdsenseList）
const dailyTrendData = computed(() => {
  const list = filteredAdsenseList.value
  const byDate = {}
  list.forEach((row) => {
    const d = getRowDate(row)
    if (!d) return
    if (!byDate[d]) {
      byDate[d] = { date: d, revenue: 0, pageView: 0, adViewCount: 0, clickCount: 0 }
    }
    byDate[d].revenue += Number(row.revenue) || 0
    byDate[d].pageView += Number(row.pageView) || 0
    byDate[d].adViewCount += Number(row.adViewCount) || 0
    byDate[d].clickCount += Number(row.clickCount) || 0
  })
  let dates = Object.keys(byDate).sort()
  if (dateStart.value && dateEnd.value && dates.length > 0) {
    const start = dateStart.value
    const end = dateEnd.value
    const fullDates = []
    const d = new Date(start)
    const endD = new Date(end)
    while (d <= endD) {
      fullDates.push(d.toISOString().slice(0, 10))
      d.setDate(d.getDate() + 1)
    }
    dates = fullDates
  }
  return {
    dates,
    revenue: dates.map((d) => (byDate[d] ? byDate[d].revenue : 0)),
    pageView: dates.map((d) => (byDate[d] ? byDate[d].pageView : 0)),
    adViewCount: dates.map((d) => (byDate[d] ? byDate[d].adViewCount : 0)),
    clickCount: dates.map((d) => (byDate[d] ? byDate[d].clickCount : 0))
  }
})

const dailyTrendRows = computed(() => {
  const { dates, revenue, pageView, adViewCount, clickCount } = dailyTrendData.value
  return dates.map((date, idx) => ({
    date,
    revenue: revenue[idx] ?? 0,
    pageView: pageView[idx] ?? 0,
    adViewCount: adViewCount[idx] ?? 0,
    clickCount: clickCount[idx] ?? 0
  }))
})

// 表格底部汇总行：按列返回汇总文案（与 el-table 的 summary-method 约定一致）
function getSummaries({ columns, data }) {
  const sums = []
  columns.forEach((col, index) => {
    const prop = col.property
    if (index === 0) {
      sums.push('汇总')
      return
    }
    if (prop === 'gameChannel' || prop === 'date' || prop === undefined) {
      sums.push('')
      return
    }
    if (summaryTotals.value[prop] !== undefined) {
      sums.push(formatNumber(summaryTotals.value[prop]))
      return
    }
    sums.push('')
  })
  return sums
}

// 按渠道聚合指定维度的数据
function getChannelPieData(dimensionKey) {
  const opt = PIE_DIMENSION_OPTIONS[dimensionKey] || PIE_DIMENSION_OPTIONS.revenue
  const map = {}
  adsenseList.value.forEach((row) => {
    const channels = getRowChannels(row)
    const bucket = channels.length ? channels : ['未分类']
    bucket.forEach((channel) => {
      if (!map[channel]) map[channel] = 0
      map[channel] += Number(row[opt.key]) || 0
    })
  })
  return Object.entries(map).map(([name, value]) => ({
    name,
    value: opt.isDecimal ? Math.round(value * 100) / 100 : Math.round(value)
  }))
}

// 渲染三个饼图
function renderCharts() {
  const configs = [
    { ref: pieChartRef1, instanceKey: 'pieChartInstance1', dimension: pieChartDimension1.value },
    { ref: pieChartRef2, instanceKey: 'pieChartInstance2', dimension: pieChartDimension2.value },
    { ref: pieChartRef3, instanceKey: 'pieChartInstance3', dimension: pieChartDimension3.value }
  ]
  const instances = [pieChartInstance1, pieChartInstance2, pieChartInstance3]
  configs.forEach((cfg, i) => {
    const el = cfg.ref?.value
    if (!el) return
    const opt = PIE_DIMENSION_OPTIONS[cfg.dimension] || PIE_DIMENSION_OPTIONS.revenue
    const data = getChannelPieData(cfg.dimension)
    if (!instances[i]) {
      if (i === 0) pieChartInstance1 = echarts.init(el)
      else if (i === 1) pieChartInstance2 = echarts.init(el)
      else pieChartInstance3 = echarts.init(el)
    }
    const instance = i === 0 ? pieChartInstance1 : i === 1 ? pieChartInstance2 : pieChartInstance3
    instance.setOption({
      tooltip: { trigger: 'item', formatter: `{b}\n${opt.label}: {c}\n占比: {d}%` },
      legend: { bottom: 8, left: 'center' },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        data,
        label: { show: true, formatter: `{b}\n${opt.label}: {c}\n({d}%)` },
        emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.2)' } }
      }]
    }, true)
  })
}

// URL维度数据：按URL分组
const urlGroupedData = computed(() => {
  const map = {}
  adsenseList.value.forEach((row) => {
    const url = row.url || '未分类'
    if (!map[url]) {
      map[url] = {
        url,
        revenue: 0,
        pageView: 0,
        pageViewRevenuePerTD: 0,
        adViewCount: 0,
        adViewRevenuePerTD: 0,
        activeView: 0,
        clickCount: 0
      }
    }
    map[url].revenue += Number(row.revenue) || 0
    map[url].pageView += Number(row.pageView) || 0
    map[url].pageViewRevenuePerTD += Number(row.pageViewRevenuePerTD) || 0
    map[url].adViewCount += Number(row.adViewCount) || 0
    map[url].adViewRevenuePerTD += Number(row.adViewRevenuePerTD) || 0
    map[url].activeView += Number(row.activeView) || 0
    map[url].clickCount += Number(row.clickCount) || 0
  })
  return Object.values(map)
})

// 柱状图 URL 选项（用于多选下拉）：value=完整url，label=去域名后的完整路径
function getUrlDisplayPath(url) {
  if (!url || url === '未分类') return url || '未分类'
  try {
    if (url.includes('://')) {
      const urlObj = new URL(url)
      return urlObj.pathname + urlObj.search + urlObj.hash
    }
  } catch (e) {
    // ignore
  }
  return url
}

const urlOptions = computed(() => {
  return urlGroupedData.value.map(item => ({
    value: item.url || '未分类',
    label: getUrlDisplayPath(item.url)
  }))
})

// 渲染柱状图
function renderBarChart() {
  if (!barChartRef.value || !selectedColumns.value.length) {
    if (barChartInstance) {
      barChartInstance.setOption({ series: [] }, true)
    }
    return
  }
  
  let data = urlGroupedData.value
  // URL 维度过滤：若已勾选 URL，只展示勾选的
  if (selectedUrls.value.length > 0) {
    const set = new Set(selectedUrls.value)
    data = data.filter(item => set.has(item.url || '未分类'))
  }
  if (!data.length) {
    if (barChartInstance) barChartInstance.setOption({ series: [] }, true)
    return
  }
  
  // 按第一个选中的列排序，只取前 N 条避免柱子过密
  const MAX_BAR_ITEMS = 25
  const sortedData = [...data].sort((a, b) => {
    const firstKey = columnOptions[selectedColumns.value[0]]?.key
    return (b[firstKey] || 0) - (a[firstKey] || 0)
  }).slice(0, MAX_BAR_ITEMS)
  
  const urls = sortedData.map(item => {
    // 去掉域名部分，只保留路径；底部 X 轴标签做截断
    let url = item.url || '未分类'
    try {
      if (url.includes('://')) {
        const urlObj = new URL(url)
        url = urlObj.pathname + urlObj.search + urlObj.hash
      }
    } catch (e) {
      // 如果解析失败，保持原样
    }
    const maxLen = 30
    return url.length > maxLen ? url.substring(0, maxLen) + '...' : url
  })
  
  // 构建系列数据
  const series = selectedColumns.value.map(colKey => {
    const option = columnOptions[colKey]
    if (!option) return null
    
    return {
      name: option.label,
      type: 'bar',
      barMaxWidth: 48,
      label: {
        show: true,
        position: 'top',
        formatter: (params) => {
          const v = params.value
          return typeof v === 'number' && v % 1 !== 0 ? v.toFixed(2) : v
        }
      },
      data: sortedData.map(item => {
        const value = item[option.key] || 0
        // 保留适当的小数位
        return colKey === 'revenue' || colKey.includes('Revenue') || colKey === 'activeView'
          ? Math.round(value * 100) / 100
          : Math.round(value)
      })
    }
  }).filter(Boolean)
  
  if (!barChartInstance) {
    barChartInstance = echarts.init(barChartRef.value)
  }
  
  barChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        let url = sortedData[params[0].dataIndex]?.url || '未分类'
        // 去掉域名部分，只保留路径
        try {
          if (url.includes('://')) {
            const urlObj = new URL(url)
            url = urlObj.pathname + urlObj.search + urlObj.hash
          }
        } catch (e) {
          // 如果解析失败，保持原样
        }
        let result = `<div style="margin-bottom: 4px;"><strong>${url}</strong></div>`
        params.forEach(param => {
          const value = typeof param.value === 'number' 
            ? (param.value % 1 === 0 ? param.value : param.value.toFixed(2))
            : param.value
          result += `<div>${param.seriesName}: ${value}</div>`
        })
        return result
      }
    },
    legend: {
      data: selectedColumns.value.map(key => columnOptions[key]?.label).filter(Boolean),
      bottom: 8
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '22%',
      top: '8%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: urls,
      axisLabel: {
        rotate: 45,
        interval: 0,
        fontSize: 12
      }
    },
    yAxis: {
      type: 'value'
    },
    series: series
  }, true)
}

// 汇总页折线图：按日展示收入、页面浏览、广告浏览、点击次数
function renderLineChart() {
  const el = lineChartRef.value
  if (!el) return
  const { dates, revenue, pageView, adViewCount, clickCount } = dailyTrendData.value
  if (!dates.length) {
    if (lineChartInstance) lineChartInstance.setOption({ series: [] }, true)
    return
  }
  if (!lineChartInstance) {
    lineChartInstance = echarts.init(el)
  }
  const series = [
    { name: '收入', type: 'line', smooth: true, data: revenue, yAxisIndex: 0 },
    { name: '页面浏览量', type: 'line', smooth: true, data: pageView, yAxisIndex: 1 },
    { name: '广告浏览量', type: 'line', smooth: true, data: adViewCount, yAxisIndex: 1 },
    { name: '点击次数', type: 'line', smooth: true, data: clickCount, yAxisIndex: 1 }
  ]
  lineChartInstance.setOption({
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' }
    },
    legend: { data: ['收入', '页面浏览量', '广告浏览量', '点击次数'], bottom: 8 },
    grid: { left: '3%', right: '8%', bottom: '18%', top: '10%', containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: dates },
    yAxis: [
      { type: 'value', name: '收入', position: 'left' },
      { type: 'value', name: '浏览量/次数', position: 'right' }
    ],
    series
  }, true)
  // 汇总 tab 刚显示时容器可能尚未有尺寸，多次 resize 确保图表正确绘制
  nextTick(() => {
    setTimeout(() => {
      lineChartInstance?.resize()
      setTimeout(() => lineChartInstance?.resize(), 150)
    }, 80)
  })
}

// 切换到图表 tab 时渲染
watch([activeTab, adsenseList, pieChartDimension1, pieChartDimension2, pieChartDimension3, urlGroupedData, selectedColumns, selectedUrls], () => {
  if (activeTab.value === 'charts') {
    nextTick(() => {
      renderCharts()
      renderBarChart()
    })
  }
})
// 切换到汇总 tab 或每日数据变化时渲染折线图（延迟一帧确保 tab 内容器已有尺寸）
watch([activeTab, dailyTrendData], () => {
  if (activeTab.value !== 'summary' || trendViewMode.value !== 'chart') return
  nextTick(() => {
    setTimeout(renderLineChart, 50)
  })
})

watch(trendViewMode, (mode) => {
  if (activeTab.value === 'summary' && mode === 'chart') {
    nextTick(() => {
      setTimeout(renderLineChart, 50)
    })
  }
})

// 格式化数字
const formatNumber = (value) => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  return value
}

/** 根据当前导出明细中的日期字段得到文件名：游戏收入_最早日期_最晚日期.xlsx */
function getExportExcelFileName(list) {
  const dates = [
    ...new Set(
      list
        .map((row) => getRowDate(row))
        .filter((d) => d && String(d).trim())
    )
  ].sort()
  if (dates.length === 0) {
    return '游戏收入_无日期.xlsx'
  }
  const earliest = dates[0]
  const latest = dates[dates.length - 1]
  const suffix = earliest === latest ? earliest : `${earliest}_${latest}`
  return `游戏收入_${suffix}.xlsx`
}

const EXCEL_SUBTOTAL_FILL = 'FFDEEBF7' // 浅蓝底：本日合计
const EXCEL_GRAND_TOTAL_FILL = 'FFE8F5E9' // 浅绿底：全部汇总，与本日合计区分

function sumNumericRows(rows, keys) {
  const acc = {}
  keys.forEach((k) => {
    acc[k] = 0
  })
  rows.forEach((row) => {
    keys.forEach((k) => {
      acc[k] += Number(row[k]) || 0
    })
  })
  return acc
}

/** 导出：明细按日期分组，每日明细后插入「本日收入合计」行（浅底高亮）；另含按日汇总表 */
async function exportToExcel() {
  const list = filteredAdsenseList.value
  if (!list.length) {
    ElMessage.warning('没有可导出的数据')
    return
  }

  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'rabigame-ui'

  const detailSheet = workbook.addWorksheet('明细', {
    views: [{ state: 'frozen', ySplit: 1 }]
  })

  const headers = [
    'URL',
    '游戏渠道',
    '日期',
    '收入',
    '页面浏览量',
    '页面浏览量收入/TD',
    '广告浏览量',
    '广告浏览量收入/TD',
    '活跃视图',
    '点击次数'
  ]
  const headerRow = detailSheet.addRow(headers)
  headerRow.font = { bold: true }
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF2F2F2' }
    }
  })
  detailSheet.columns = [
    { width: 42 },
    { width: 12 },
    { width: 14 },
    { width: 12 },
    { width: 14 },
    { width: 18 },
    { width: 14 },
    { width: 18 },
    { width: 12 },
    { width: 12 }
  ]

  const numericKeys = [
    'revenue',
    'pageView',
    'pageViewRevenuePerTD',
    'adViewCount',
    'adViewRevenuePerTD',
    'activeView',
    'clickCount'
  ]

  const byDate = new Map()
  for (const row of list) {
    const d = getRowDate(row) || ''
    const key = d || '__EMPTY_DATE__'
    if (!byDate.has(key)) byDate.set(key, [])
    byDate.get(key).push(row)
  }

  const sortedDateKeys = [...byDate.keys()].sort((a, b) => {
    if (a === '__EMPTY_DATE__') return 1
    if (b === '__EMPTY_DATE__') return -1
    return a.localeCompare(b)
  })

  for (const dateKey of sortedDateKeys) {
    const rows = byDate.get(dateKey)
    rows.sort((a, b) => (a.url || '').localeCompare(b.url || ''))

    for (const row of rows) {
      detailSheet.addRow([
        row.url ?? '',
        getRowChannelsText(row),
        getRowDate(row),
        Number(row.revenue) || 0,
        Number(row.pageView) || 0,
        Number(row.pageViewRevenuePerTD) || 0,
        Number(row.adViewCount) || 0,
        Number(row.adViewRevenuePerTD) || 0,
        Number(row.activeView) || 0,
        Number(row.clickCount) || 0
      ])
    }

    const sums = sumNumericRows(rows, numericKeys)
    const dateLabel =
      dateKey === '__EMPTY_DATE__' ? '（无日期）' : dateKey
    const subtotalRow = detailSheet.addRow([
      '',
      '',
      `本日收入合计 · ${dateLabel}`,
      sums.revenue,
      sums.pageView,
      sums.pageViewRevenuePerTD,
      sums.adViewCount,
      sums.adViewRevenuePerTD,
      sums.activeView,
      sums.clickCount
    ])
    subtotalRow.font = { bold: true }
    subtotalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: EXCEL_SUBTOTAL_FILL }
      }
    })
    subtotalRow.getCell(4).numFmt = '0.00'
  }

  const grandTotals = sumNumericRows(list, numericKeys)
  const grandRow = detailSheet.addRow([
    '',
    '',
    '全部收入合计（当前筛选）',
    grandTotals.revenue,
    grandTotals.pageView,
    grandTotals.pageViewRevenuePerTD,
    grandTotals.adViewCount,
    grandTotals.adViewRevenuePerTD,
    grandTotals.activeView,
    grandTotals.clickCount
  ])
  grandRow.font = { bold: true }
  grandRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: EXCEL_GRAND_TOTAL_FILL }
    }
  })
  grandRow.getCell(4).numFmt = '0.00'

  const summarySheet = workbook.addWorksheet('按日汇总')
  summarySheet.addRow(['日期', '总收入', '页面浏览量', '广告浏览量', '点击次数'])
  summarySheet.getRow(1).font = { bold: true }
  const { dates, revenue, pageView, adViewCount, clickCount } = dailyTrendData.value
  dates.forEach((d, i) => {
    summarySheet.addRow([
      d,
      revenue[i] ?? 0,
      pageView[i] ?? 0,
      adViewCount[i] ?? 0,
      clickCount[i] ?? 0
    ])
  })
  summarySheet.columns = [{ width: 14 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 12 }]
  summarySheet.getColumn(2).numFmt = '0.00'

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    getExportExcelFileName(list)
  )
  ElMessage.success('导出成功')
}

async function exportDailyTrendTable() {
  const rows = dailyTrendRows.value
  if (!rows.length) {
    ElMessage.warning('没有可导出的按天汇总数据')
    return
  }
  const workbook = new ExcelJS.Workbook()
  workbook.creator = 'rabigame-ui'
  const sheet = workbook.addWorksheet('每日汇总')
  const headers = ['日期', '收入', '页面浏览量', '广告浏览量', '点击次数']
  const headerRow = sheet.addRow(headers)
  headerRow.font = { bold: true }
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFF2F2F2' }
    }
  })
  rows.forEach((r) => {
    sheet.addRow([r.date, r.revenue, r.pageView, r.adViewCount, r.clickCount])
  })
  sheet.columns = [{ width: 14 }, { width: 12 }, { width: 14 }, { width: 14 }, { width: 12 }]
  sheet.getColumn(2).numFmt = '0.00'
  const buffer = await workbook.xlsx.writeBuffer()
  const start = rows[0].date
  const end = rows[rows.length - 1].date
  const suffix = start === end ? start : `${start}_${end}`
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `每日汇总_${suffix}.xlsx`
  )
  ElMessage.success('按天汇总表导出成功')
}

// 加载数据（不限制返回条数）
const loadData = async () => {
  if (!dateStart.value || !dateEnd.value) {
    adsenseList.value = []
    ElMessage.warning('AdSense 后台拉取请先选择日期范围')
    return
  }
  loading.value = true
  try {
    const result = await AdSenseApi.getList({
      skip: 0,
      dateStart: dateStart.value,
      dateEnd: dateEnd.value,
      gameChannel: selectedChannel.value || undefined,
      source: sourceMode.value
    })
    adsenseList.value = result.list || []
  } catch (error) {
    console.error('Failed to load AdSense data:', error)
    ElMessage.error('加载数据失败：' + (error.response?.data?.status?.message || error.message))
  } finally {
    loading.value = false
  }
}

const handleSyncRange = async () => {
  if (sourceMode.value !== 'google') {
    ElMessage.warning('请先切换到 AdSense 实时来源，再执行同步')
    return
  }
  const rows = filteredAdsenseList.value || []
  if (!rows.length) {
    ElMessage.warning('当前表格无可同步数据')
    return
  }

  const dateMap = new Map()
  rows.forEach((row) => {
    const date = getRowDate(row)
    if (!date) return
    if (!dateMap.has(date)) {
      dateMap.set(date, [])
    }
    dateMap.get(date).push(row)
  })
  if (dateMap.size === 0) {
    ElMessage.warning('当前数据缺少日期字段，无法同步')
    return
  }

  syncing.value = true
  try {
    let total = 0
    let totalSuccess = 0
    let totalFailed = 0
    for (const [date, dayRows] of dateMap.entries()) {
      const payloadRows = dayRows.map((item) => ({
        url: item.url,
        revenue: item.revenue,
        pageView: item.pageView,
        pageViewRevenuePerTD: item.pageViewRevenuePerTD,
        adViewCount: item.adViewCount,
        adViewRevenuePerTD: item.adViewRevenuePerTD,
        activeView: item.activeView,
        clickCount: item.clickCount,
        gameChannel: item.gameChannel,
        gameChannels: item.gameChannels,
        date: getRowDate(item)
      }))
      const result = await AdSenseApi.upload({
        date,
        jsonString: JSON.stringify(payloadRows)
      })
      total += result.total || payloadRows.length
      totalSuccess += result.successCount || 0
      totalFailed += result.failedCount || 0
    }
    ElMessage.success(
      `同步完成：共 ${dateMap.size} 天，写入 ${totalSuccess} 条，失败 ${totalFailed} 条（总计 ${total} 条）`
    )
  } catch (error) {
    console.error('Failed to sync AdSense data:', error)
    ElMessage.error('同步失败：' + (error.response?.data?.status?.message || error.message))
  } finally {
    syncing.value = false
  }
}

// 日期范围变化
const handleDateRangeChange = (value) => {
  if (value && value.length === 2) {
    dateStart.value = value[0]
    dateEnd.value = value[1]
  } else {
    dateStart.value = null
    dateEnd.value = null
  }
  loadData()
}

// 渠道变化
const handleChannelChange = () => {
  loadData()
}

// 清除筛选（日期 + 渠道）
const clearFilter = () => {
  dateRange.value = null
  dateStart.value = null
  dateEnd.value = null
  selectedChannel.value = ''
  loadData()
}

// 删除记录
const handleDelete = async (id) => {
  try {
    await ElMessageBox.confirm('确定要删除这条记录吗？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    loading.value = true
    try {
      await AdSenseApi.delete(id)
      ElMessage.success('删除成功')
      loadData()
    } catch (error) {
      console.error('Failed to delete AdSense data:', error)
      ElMessage.error('删除失败：' + (error.response?.data?.status?.message || error.message))
    } finally {
      loading.value = false
    }
  } catch {
    // 用户取消
  }
}

// 初始化
onMounted(async () => {
  // 默认最近 7 天，避免首次进入无日期导致无法从 AdSense 拉取
  const today = new Date()
  const start = new Date(today)
  start.setDate(today.getDate() - 6)
  const fmt = (d) => d.toISOString().slice(0, 10)
  dateStart.value = fmt(start)
  dateEnd.value = fmt(today)
  dateRange.value = [dateStart.value, dateEnd.value]
  await loadChannelRules()
  loadData()
})
</script>

<style scoped>
.analytics-container {
  padding: 20px;
}

.page-title {
  margin: 0 0 20px 0;
  font-size: 24px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.action-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--el-bg-color);
  border-radius: 4px;
}

.action-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.action-label {
  color: var(--el-text-color-regular);
  white-space: nowrap;
}

@media (max-width: 1400px) {
  .action-bar {
    align-items: flex-start;
  }
}

.table-section {
  background-color: var(--el-bg-color);
  border-radius: 4px;
  padding: 15px;
}

.table-footer {
  margin-top: 12px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.analytics-main-loading {
  position: relative;
  min-height: 360px;
}

.analytics-tabs {
  margin-top: 0;
}

.analytics-tabs :deep(.el-tabs__header) {
  margin-bottom: 16px;
}

.charts-section {
  background-color: var(--el-bg-color);
  border-radius: 4px;
  padding: 20px;
  min-height: 400px;
}

.chart-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.chart-card {
  flex: 1;
  min-width: 320px;
  max-width: 480px;
  background: var(--el-bg-color-page);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.chart-card-pie {
  max-width: 520px;
}

.pie-chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.pie-chart-header .chart-title {
  margin-bottom: 0;
}

.chart-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
  color: var(--el-text-color-primary);
}

.chart-dom {
  width: 100%;
  height: 280px;
}

.charts-empty {
  text-align: center;
  color: var(--el-text-color-secondary);
  padding: 40px;
  font-size: 14px;
}

.summary-section {
  background-color: var(--el-bg-color);
  border-radius: 4px;
  padding: 24px;
  min-height: 200px;
}

.summary-desc {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  margin-bottom: 20px;
}

.summary-cards {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.summary-card {
  flex: 1;
  min-width: 200px;
  max-width: 280px;
  background: var(--el-bg-color-page);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.summary-card .summary-label {
  font-size: 14px;
  color: var(--el-text-color-secondary);
  margin-bottom: 8px;
}

.summary-card .summary-value {
  font-size: 22px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.summary-empty {
  text-align: center;
  color: var(--el-text-color-secondary);
  padding: 40px;
  font-size: 14px;
}

.line-chart-section {
  margin-top: 28px;
  background: var(--el-bg-color-page);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.line-chart-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 0;
  color: var(--el-text-color-primary);
}

.line-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.line-chart-tools {
  display: flex;
  align-items: center;
  gap: 10px;
}

.line-chart-dom {
  width: 100%;
  height: 360px;
  min-height: 360px;
}

.bar-chart-section {
  margin-top: 32px;
  background: var(--el-bg-color-page);
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.bar-chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.bar-chart-header .chart-title {
  margin-bottom: 0;
}

.bar-chart-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.bar-chart-hint {
  font-size: 13px;
  color: var(--el-text-color-secondary);
  font-weight: normal;
}

.url-filter-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.url-filter-row .url-select-full {
  flex: 1;
  min-width: 0;
}
.url-filter-row .url-select-full :deep(.el-select__wrapper) {
  width: 100%;
}

.column-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.selector-label {
  font-size: 14px;
  color: var(--el-text-color-regular);
  white-space: nowrap;
}

.bar-chart-dom {
  width: 100%;
  height: 450px;
  min-height: 450px;
}

.url-cell .url-text {
  display: block;
  word-break: break-all;
  white-space: normal;
  line-height: 1.4;
}
</style>

<!-- URL 下拉列表高度：便于查看全部 URL（popper 挂载在 body） -->
<style>
.url-select-dropdown.el-select-dropdown {
  max-height: 70vh;
}
.url-select-dropdown .el-select-dropdown__list {
  max-height: 70vh;
}
</style>
